import { useState } from "react";
import { Save, Loader2, CheckCircle2, XCircle, Key } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Admin-only form to inject Hetzner S3 credentials directly into our Mongo
 * runtime_config collection. Workaround for the Emergent Production
 * secrets-store persistence bug — env-vars still win when present.
 */
const HetznerConfigForm = ({ onSaved }) => {
  const [form, setForm] = useState({
    endpoint: "https://fsn1.your-objectstorage.com",
    region: "fsn1",
    access_key: "",
    secret_key: "",
    bucket: "novatusenergie",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.access_key.trim() || !form.secret_key.trim()) {
      toast.error("Access Key und Secret Key sind Pflicht");
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/recordings/configure", form);
      setResult(data);
      if (data.healthy) {
        toast.success("Hetzner-Speicher aktiv — Verbindung erfolgreich getestet");
        onSaved?.();
      } else {
        toast.error(`Gespeichert, aber Verbindung fehlgeschlagen: ${data.health_error || "unbekannter Fehler"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nv-card p-5 mt-4 border-novatus-green/40" data-testid="hetzner-config-form">
      <div className="flex items-start gap-3">
        <Key className="w-5 h-5 text-novatus-green shrink-0 mt-1" />
        <div className="flex-1">
          <p className="label-eyebrow text-novatus-green">Notfall-Konfiguration</p>
          <h3 className="font-display font-bold text-white text-base mt-1">
            Hetzner-S3 direkt in DB speichern
          </h3>
          <p className="text-xs text-novatus-text-2 mt-2 leading-relaxed">
            Workaround, falls die Werte im Emergent Secrets-Tab nicht persistent
            bleiben. Die Werte landen in unserer eigenen MongoDB
            (<code className="text-novatus-green">runtime_config</code>) und werden
            beim Backend-Start sowie sofort nach dem Speichern aktiviert. ENV-Vars
            haben Vorrang, wenn sie gesetzt sind.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3">
        {[
          { key: "endpoint", label: "ENDPOINT", placeholder: "https://fsn1.your-objectstorage.com" },
          { key: "region", label: "REGION", placeholder: "fsn1" },
          { key: "bucket", label: "BUCKET", placeholder: "novatusenergie" },
          { key: "access_key", label: "ACCESS_KEY", placeholder: "20 Zeichen, beginnt oft mit OC…", required: true },
          { key: "secret_key", label: "SECRET_KEY", placeholder: "40 Zeichen", required: true, mono: true },
        ].map((f) => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider font-bold text-novatus-text-3 block mb-1">
              {f.label} {f.required && <span className="text-amber-400">*</span>}
            </label>
            <input
              type="text"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={form[f.key]}
              onChange={update(f.key)}
              placeholder={f.placeholder}
              className={`w-full h-10 rounded-lg bg-novatus-card-2 border border-novatus text-white px-3 text-sm focus:border-novatus-green focus:outline-none ${f.mono ? "font-mono text-xs" : ""}`}
              data-testid={`hetzner-${f.key}-input`}
            />
          </div>
        ))}

        <button
          type="submit"
          disabled={saving}
          className="btn-green w-full h-11 rounded-lg inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50 mt-2"
          data-testid="hetzner-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Speichere und teste Verbindung…" : "Speichern & Verbindung testen"}
        </button>

        {result && (
          <div
            className={`mt-3 rounded-lg p-3 text-xs flex items-start gap-2 ${
              result.healthy
                ? "bg-novatus-green/10 border border-novatus-green/40 text-novatus-green"
                : "bg-amber-500/10 border border-amber-500/40 text-amber-300"
            }`}
            data-testid="hetzner-result"
          >
            {result.healthy ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-bold">
                {result.healthy ? "Erfolg" : "Speichern OK — aber S3-Verbindung schlug fehl"}
              </p>
              <p className="mt-1 leading-relaxed">
                Aktualisierte Felder: {result.fields_updated?.join(", ") || "—"}
                {result.health_error && (
                  <span className="block mt-1 font-mono text-[11px] break-all opacity-90">
                    {result.health_error}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
      </form>

      <p className="text-[10px] text-novatus-text-3 mt-3 leading-relaxed">
        Hinweis: Die Werte sind ausschließlich Admins zugänglich und werden
        verschlüsselt mit der MongoDB übertragen. Sie ersetzen den
        Emergent-Secrets-Tab nur als Fallback. Sobald Emergent Support den
        Persistenz-Bug behebt, kannst du diesen DB-Eintrag entfernen.
      </p>
    </div>
  );
};

export default HetznerConfigForm;
